import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Saying what just happened — RX-UI-04.
 *
 * Until now each screen invented its own: a grey "Saved." that appeared
 * beside a button, a red banner that in one case rendered *underneath* a
 * modal overlay where nobody could see it, and in several places nothing at
 * all — the row simply changed and you were left to infer it worked.
 *
 * A few rules this encodes, because they are the ones that get broken:
 *
 * - **A failure never disappears on a timer.** A success can: the thing you
 *   wanted happened, and the evidence is on screen behind the toast. A
 *   failure is the only record that the thing did NOT happen, so it stays
 *   until dismissed.
 * - **It is announced, not just drawn.** `role="status"` for a success and
 *   `role="alert"` for a failure, so a screen reader says it. A toast nobody
 *   can hear is a toast that did not happen for part of the audience.
 *   `aria-live` is on the container, which must exist in the DOM before the
 *   message is added or nothing is announced at all.
 * - **It does not steal focus.** A person mid-keystroke should not be
 *   interrupted by a confirmation of what they already did.
 * - **It is never the only place a failure appears** when the failing action
 *   lives inside a dialog — that dialog owns its own error, and this is an
 *   addition rather than a replacement.
 */
export type ToastTone = 'success' | 'error' | 'info';

export interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
  /** Shown smaller underneath: the provider's own words, an id, a count. */
  detail?: string;
}

interface ToastApi {
  success: (message: string, detail?: string) => void;
  error: (message: string, detail?: string) => void;
  info: (message: string, detail?: string) => void;
}

const Ctx = createContext<ToastApi | null>(null);

/**
 * Outside the tree, so a hook is not needed to say "saved".
 *
 * A component that wants to report a result usually wants to do it from an
 * async callback, sometimes from a module that is not a component at all.
 * Threading a hook through every one of those is how half of them end up
 * saying nothing.
 */
let publish: ((t: Omit<Toast, 'id'>) => void) | null = null;

export const toast: ToastApi = {
  success: (message, detail) => publish?.({ tone: 'success', message, detail }),
  error: (message, detail) => publish?.({ tone: 'error', message, detail }),
  info: (message, detail) => publish?.({ tone: 'info', message, detail }),
};

/** How long a success stays. Failures never auto-dismiss. */
const SUCCESS_MS = 4000;

export function ToastHost({ children }: { children?: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const next = useRef(1);

  const add = useCallback((t: Omit<Toast, 'id'>) => {
    const id = next.current++;
    setToasts((current) => [...current, { ...t, id }]);
    if (t.tone !== 'error') {
      setTimeout(() => setToasts((c) => c.filter((x) => x.id !== id)), SUCCESS_MS);
    }
  }, []);

  useEffect(() => {
    publish = add;
    return () => { publish = null; };
  }, [add]);

  const api = useMemo<ToastApi>(
    () => ({
      success: (m, d) => add({ tone: 'success', message: m, detail: d }),
      error: (m, d) => add({ tone: 'error', message: m, detail: d }),
      info: (m, d) => add({ tone: 'info', message: m, detail: d }),
    }),
    [add],
  );

  const dismiss = (id: number) => setToasts((c) => c.filter((x) => x.id !== id));

  return (
    <Ctx.Provider value={api}>
      {children}
      {/*
        The live region is always mounted, even with nothing in it. Screen
        readers announce changes to a region that already existed; one added
        to the DOM at the same moment as its first message is frequently
        missed entirely.

        z-50 puts it above the z-40 dialogs, so a toast raised by something
        inside a dialog is not drawn behind it.
      */}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 p-4 sm:items-end"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            role={t.tone === 'error' ? 'alert' : 'status'}
            className={`pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl border px-4 py-3 shadow-lg ${
              t.tone === 'error'
                ? 'border-red-500/40 bg-red-500/10 text-red-600'
                : t.tone === 'success'
                  ? 'border-(--accent)/40 bg-(--accent-soft) text-(--accent)'
                  : 'border-(--line) bg-(--panel) text-(--fg)'
            }`}
          >
            <span aria-hidden="true" className="mt-0.5 shrink-0">
              {t.tone === 'error' ? '!' : t.tone === 'success' ? '✓' : 'i'}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">{t.message}</p>
              {t.detail && <p className="mt-0.5 text-xs opacity-80">{t.detail}</p>}
            </div>
            <button
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss"
              className="min-h-9 min-w-9 shrink-0 rounded-lg text-sm opacity-70 hover:opacity-100"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

/** For components that would rather take the api as a value. */
export function useToast(): ToastApi {
  return useContext(Ctx) ?? toast;
}
